import { supabase } from './supabase';
import {
  DEFAULT_FORCE_FAMILY_DATA,
  buildForceBlock,
  computeForcePointMetrics,
  finiteOrNull,
  forceValueToBase,
  mergeForceFamilyData,
  resolveForceProfile,
  suggestForceSetpoints,
  validateForceRows,
} from './force-calibration-profile';

export {
  getTodayDailyEnvironmentLog,
  upsertTodayDailyEnvironmentLog,
} from './api-pressure-manometer-pilot';

export const DEFAULT_FORCE_VALIDITY_MONTHS = 12;
const VALIDITY_DAYS_BY_MONTHS = { 3: 90, 6: 181, 12: 365, 24: 730 };

function sanitizePatch(patch = {}) {
  return Object.fromEntries(Object.entries(patch).filter(([, value]) => (
    value !== undefined && !(typeof value === 'number' && !Number.isFinite(value))
  )));
}

async function currentUserId() {
  return (await supabase.auth.getUser())?.data?.user?.id ?? null;
}

export function resolveForceValidityDays(months = DEFAULT_FORCE_VALIDITY_MONTHS) {
  const value = Number(months);
  if (!Number.isFinite(value) || value <= 0) return VALIDITY_DAYS_BY_MONTHS[DEFAULT_FORCE_VALIDITY_MONTHS];
  return VALIDITY_DAYS_BY_MONTHS[value] ?? Math.round((365 / 12) * value);
}

export function addForceValidityDaysToIsoDate(isoDate, months = DEFAULT_FORCE_VALIDITY_MONTHS) {
  const base = String(isoDate || '').slice(0, 10);
  if (!base) return null;
  const date = new Date(`${base}T12:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  date.setDate(date.getDate() + resolveForceValidityDays(months));
  return date.toISOString().slice(0, 10);
}

async function getTodayEnvironment() {
  const { getTodayDailyEnvironmentLog } = await import('./api-pressure-manometer-pilot');
  try {
    return await getTodayDailyEnvironmentLog();
  } catch {
    return null;
  }
}

export async function createForceSession({
  instrumentId,
  serviceOrderId = null,
  serviceOrderCode = null,
  certificateType = 'NO_SAC',
  validityMonths = DEFAULT_FORCE_VALIDITY_MONTHS,
  templateCode = '',
}) {
  if (!instrumentId) throw new Error('instrumentId es requerido');

  const { data: instrument, error: instrumentError } = await supabase
    .from('instruments')
    .select(`id, tag, lab_id, brand, model, serial, type, unit, resolution, range_min, range_max, tolerance, calibration_place,
             category:category_id ( name ),
             client:client_id ( id, client_number, name, address, locality, province )`)
    .eq('id', instrumentId)
    .single();
  if (instrumentError) throw instrumentError;

  const profile = resolveForceProfile(instrument, templateCode);
  const sourceUnit = instrument?.unit || profile.unit;
  const rangeMin = forceValueToBase(instrument?.range_min, sourceUnit, profile.subfamily);
  const rangeMax = forceValueToBase(instrument?.range_max, sourceUnit, profile.subfamily);
  const resolution = forceValueToBase(instrument?.resolution, sourceUnit, profile.subfamily);
  if (rangeMax === null || rangeMax <= 0) {
    throw new Error('El instrumento necesita rango máximo y unidad antes de iniciar la calibración.');
  }

  const environment = await getTodayEnvironment();
  const tolerance = Math.abs(finiteOrNull(instrument?.tolerance) ?? 0);
  const directions = profile.subfamily === 'torque' ? ['CW'] : ['LOAD'];
  const familyData = mergeForceFamilyData({
    ...DEFAULT_FORCE_FAMILY_DATA,
    profile_key: profile.code,
    profile_label: profile.title,
    subfamily: profile.subfamily,
    method: profile.method,
    procedure: profile.procedure,
    point_count: profile.pointCount,
    repeat_count: profile.repeatCount,
    directions,
    tolerance_percent: tolerance || (profile.subfamily === 'torque' ? 4 : 1),
    original_instrument_unit: sourceUnit,
    values_normalized_to: profile.unit,
    environment_readings: DEFAULT_FORCE_FAMILY_DATA.environment_readings.map((row) => ({
      ...row,
      temp_c: finiteOrNull(environment?.temp_c),
      pressure_hpa: finiteOrNull(environment?.pressure_hpa),
      humidity_rh: finiteOrNull(environment?.humidity_rh),
    })),
  });

  const payload = {
    family: 'force',
    instrument_id: instrument.id,
    service_order_id: serviceOrderId,
    service_order_code: serviceOrderCode || null,
    certificate_type: certificateType,
    status: 'draft',
    template_code: profile.code,
    template_revision: profile.revision,
    client_number: instrument?.client?.client_number ? String(instrument.client.client_number) : null,
    client_name: instrument?.client?.name || null,
    client_address: instrument?.client?.address || null,
    client_city: instrument?.client?.locality || null,
    client_province: instrument?.client?.province || null,
    instrument_object: instrument?.category?.name || instrument?.type || profile.title,
    instrument_brand: instrument?.brand || null,
    instrument_model: instrument?.model || null,
    instrument_serial: instrument?.serial || null,
    instrument_tag: instrument?.tag || null,
    instrument_unit: profile.unit,
    instrument_resolution: resolution,
    instrument_range_min: rangeMin,
    instrument_range_max: rangeMax,
    instrument_class: tolerance || null,
    instrument_location: instrument?.calibration_place || 'Laboratorio',
    ambient_pressure_avg: finiteOrNull(environment?.pressure_hpa),
    ambient_temp_avg: finiteOrNull(environment?.temp_c),
    ambient_humidity_avg: finiteOrNull(environment?.humidity_rh),
    validity_months: Number(validityMonths) || DEFAULT_FORCE_VALIDITY_MONTHS,
    family_data: familyData,
    created_by: await currentUserId(),
  };

  const { data: session, error } = await supabase
    .from('calibration_sessions').insert(payload).select('*').single();
  if (error) throw error;
  const { error: blockError } = await supabase.from('calibration_session_blocks').insert(
    directions.map((direction) => ({
      ...buildForceBlock(direction, profile),
      resolution,
      session_id: session.id,
    })),
  );
  if (blockError) throw blockError;
  return session;
}

export async function listForceStandardOptions({ session, onDate } = {}) {
  const familyData = mergeForceFamilyData(session?.family_data);
  const magnitude = familyData.subfamily === 'torque' ? 'torque' : 'force';
  const max = finiteOrNull(session?.instrument_range_max);
  const min = finiteOrNull(session?.instrument_range_min);
  const effectiveMin = max === null ? min : Math.max(min ?? 0, max * 0.2);
  const { data, error } = await supabase.rpc('list_approved_reference_standard_options', {
    p_magnitude: magnitude,
    p_on_date: onDate || new Date().toISOString().slice(0, 10),
    p_range_min: effectiveMin,
    p_range_max: max,
  });
  if (error) throw error;
  return data || [];
}

export async function validateForceStandard(sessionId) {
  const { data, error } = await supabase.rpc('validate_force_session_standard', { p_session_id: sessionId });
  if (error) throw error;
  return data || { valid: false, issues: ['No se pudo validar la trazabilidad.'], configuration: {} };
}

export async function configureForceStandard(sessionId, capabilityId) {
  const { data, error } = await supabase.rpc('configure_force_session_standard', {
    p_session_id: sessionId,
    p_reference_capability_id: capabilityId,
  });
  if (error) throw error;
  return data;
}

export async function getForceSession(sessionId) {
  const { data, error } = await supabase.from('calibration_sessions')
    .select('*').eq('id', sessionId).eq('family', 'force').single();
  if (error) throw error;
  return data;
}

export async function updateForceSession(sessionId, patch) {
  const normalized = sanitizePatch(patch);
  if (normalized.family_data) normalized.family_data = mergeForceFamilyData(normalized.family_data);
  const { data, error } = await supabase.from('calibration_sessions').update({
    ...normalized,
    updated_at: new Date().toISOString(),
    updated_by: await currentUserId(),
  }).eq('id', sessionId).eq('family', 'force').select('*').single();
  if (error) throw error;
  return data;
}

export async function listForceSessionsByInstrument(instrumentId) {
  const { data, error } = await supabase.from('calibration_sessions').select('*')
    .eq('instrument_id', instrumentId).eq('family', 'force').order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function listForceBlocks(sessionId) {
  const { data, error } = await supabase.from('calibration_session_blocks').select('*')
    .eq('session_id', sessionId).order('sort_order');
  if (error) throw error;
  return data || [];
}

export async function listForcePoints(sessionId) {
  const { data, error } = await supabase.from('calibration_session_points').select('*')
    .eq('session_id', sessionId).order('point_no').order('series_no');
  if (error) throw error;
  return data || [];
}

export async function listForceResults(sessionId) {
  const { data, error } = await supabase.from('calibration_session_results').select('*')
    .eq('session_id', sessionId).order('block_id').order('point_no');
  if (error) throw error;
  return data || [];
}

export async function syncForceDirections(session, directions = []) {
  const familyData = mergeForceFamilyData(session?.family_data);
  const profile = resolveForceProfile({}, familyData.profile_key || session?.template_code);
  const required = profile.subfamily === 'torque'
    ? [...new Set(directions.map((item) => String(item).toUpperCase()).filter((item) => ['CW', 'CCW'].includes(item)))]
    : ['LOAD'];
  if (!required.length) throw new Error('Seleccioná al menos un sentido de calibración.');
  const existing = await listForceBlocks(session.id);
  const byMode = new Map(existing.map((block) => [String(block.mode).toUpperCase(), block]));
  const missing = required.filter((direction) => !byMode.has(direction));
  if (missing.length) {
    const { error } = await supabase.from('calibration_session_blocks').insert(missing.map((direction) => ({
      ...buildForceBlock(direction, profile),
      resolution: finiteOrNull(session.instrument_resolution),
      session_id: session.id,
    })));
    if (error) throw error;
  }
  const removable = existing.filter((block) => !required.includes(String(block.mode).toUpperCase()));
  if (removable.length) {
    const { error } = await supabase.from('calibration_session_blocks').delete().in('id', removable.map((block) => block.id));
    if (error) throw error;
  }
  await updateForceSession(session.id, {
    status: ['calculated', 'closed'].includes(session.status) ? 'in_progress' : session.status,
    family_data: mergeForceFamilyData({ ...familyData, directions: required }),
  });
  return listForceBlocks(session.id);
}

export function forcePointsToRows(block, points = [], session = {}) {
  const familyData = mergeForceFamilyData(session.family_data);
  const count = Number(block?.point_count) || Number(familyData.point_count) || 3;
  const repeatCount = Number(familyData.repeat_count) || 5;
  const suggested = suggestForceSetpoints(
    session.instrument_range_min,
    session.instrument_range_max,
    count,
    familyData.subfamily,
  );
  const rows = Array.from({ length: count }, (_, index) => ({
    point_no: index + 1,
    setpoint: suggested[index] ?? '',
    ...Object.fromEntries(Array.from({ length: repeatCount }, (_, repeat) => [
      [`r${repeat + 1}_pattern`, ''],
      [`r${repeat + 1}_instrument`, ''],
    ]).flat()),
  }));
  for (const point of points) {
    if (point.block_id !== block?.id) continue;
    const row = rows[Number(point.point_no) - 1];
    if (!row) continue;
    const repeat = Number(point.series_no) || 1;
    row.setpoint = point.setpoint ?? row.setpoint;
    row[`r${repeat}_pattern`] = point.pattern_reading ?? '';
    row[`r${repeat}_instrument`] = point.instrument_reading ?? '';
  }
  return rows;
}

export async function saveForcePoints(sessionId, block, rows = [], familyData = {}) {
  if (!sessionId || !block?.id) throw new Error('Sesión y bloque requeridos.');
  const merged = mergeForceFamilyData(familyData);
  const repeatCount = Number(merged.repeat_count) || 5;
  const payload = [];
  rows.forEach((row) => {
    for (let repeat = 1; repeat <= repeatCount; repeat += 1) {
      const pattern = finiteOrNull(row[`r${repeat}_pattern`]);
      const instrument = finiteOrNull(row[`r${repeat}_instrument`]);
      if (pattern === null && instrument === null) continue;
      payload.push({
        session_id: sessionId,
        block_id: block.id,
        point_no: Number(row.point_no),
        series_no: repeat,
        direction: String(block.mode || 'LOAD').toUpperCase(),
        setpoint: finiteOrNull(row.setpoint),
        pattern_reading: pattern,
        instrument_reading: instrument,
        unit: merged.values_normalized_to || 'N',
      });
    }
  });
  const { error: deleteError } = await supabase.from('calibration_session_points').delete()
    .eq('session_id', sessionId).eq('block_id', block.id);
  if (deleteError) throw deleteError;
  if (!payload.length) return [];
  const { data, error } = await supabase.from('calibration_session_points').insert(payload).select('*');
  if (error) throw error;
  return data || [];
}

export async function calculateForceResults(sessionId) {
  const validation = await validateForceStandard(sessionId);
  if (!validation?.valid) throw new Error((validation?.issues || ['La trazabilidad no es válida.']).join(' '));
  const [session, blocks, points] = await Promise.all([
    getForceSession(sessionId), listForceBlocks(sessionId), listForcePoints(sessionId),
  ]);
  if (!blocks.length) throw new Error('La sesión no tiene bloques de Fuerza / Torque.');
  const snapshot = validation?.configuration?.reference || null;
  const effectiveFamilyData = mergeForceFamilyData({
    ...session.family_data,
    standard_configuration: { reference: snapshot },
  });
  const details = [];
  for (const block of blocks) {
    const rows = forcePointsToRows(block, points, { ...session, family_data: effectiveFamilyData });
    const issues = validateForceRows(rows, effectiveFamilyData);
    if (issues.length) throw new Error(`${block.display_name || block.mode}: ${issues.join(' ')}`);
    rows.forEach((row) => {
      const metrics = computeForcePointMetrics(
        row,
        { ...session, family_data: effectiveFamilyData },
        snapshot,
        block,
      );
      if (!metrics) throw new Error(`${block.display_name || block.mode}, punto ${row.point_no}: el patrón no tiene incertidumbre utilizable.`);
      details.push(metrics);
    });
  }
  const results = details.map((metrics) => ({
    session_id: sessionId,
    block_id: metrics.block_id,
    point_no: metrics.point_no,
    pattern_mean: metrics.pattern_mean,
    instrument_mean: metrics.instrument_mean,
    error_abs: metrics.error_abs,
    error_pct: metrics.error_pct,
    hysteresis: null,
    repeatability: metrics.repeatability,
    u_standard: metrics.u_standard,
    u_drift: metrics.u_drift,
    u_resolution: metrics.u_resolution,
    u_repeatability: metrics.u_repeatability,
    u_temp: metrics.u_temp,
    u_other: Math.hypot(metrics.u_alignment, metrics.u_other),
    uc: metrics.uc,
    k: metrics.k,
    u_expanded: metrics.u_expanded,
    u_reported: metrics.u_reported,
    in_tolerance: metrics.in_tolerance,
  }));
  const { error: deleteError } = await supabase.from('calibration_session_results').delete().eq('session_id', sessionId);
  if (deleteError) throw deleteError;
  const { error } = await supabase.from('calibration_session_results').insert(results);
  if (error) throw error;
  await updateForceSession(sessionId, {
    status: 'calculated',
    family_data: mergeForceFamilyData({ ...effectiveFamilyData, result_details: details }),
  });
  return results;
}
